import React, { useState, useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import Papa from 'papaparse';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box, Button, Alert, Table, TableBody, TableCell, TableHead, TableRow,
  LinearProgress, Dialog, DialogTitle, DialogContent, DialogActions, Typography,
} from '@mui/material';
import { importCatalogPart } from '../../actions/apiCatalogParts';
import { getCatalogParts } from '../../actions/apiGetInfo';
import TruncatedPartNumber, {
  TruncatedPartDescription,
  PART_NUMBER_MAX_WIDTH,
  partDescriptionMaxWidthSx,
} from '../../components/Parts/TruncatedPartNumber';

const columnMapping = {
  'Part Number': 'part_number',
  'Part Description': 'description',
  Cost: 'price',
};

const isPartNumberHeader = (header) => columnMapping[header] === 'part_number';
const isDescriptionHeader = (header) => columnMapping[header] === 'description';

const CsvCatalogPartImporterModal = ({ open, onClose }) => {
  const dispatch = useDispatch();
  const dealership = useSelector(state => state.settings_dealerships?.installation?.subdomain || state.settings_dealerships?.current_dealership || '');
  const [csvData, setCsvData] = useState([]);
  const [headers, setHeaders] = useState([]);
  const [preview, setPreview] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [success, setSuccess] = useState(0);
  const [errors, setErrors] = useState([]);

  const reset = () => {
    setCsvData([]);
    setHeaders([]);
    setPreview([]);
    setUploading(false);
    setSuccess(0);
    setErrors([]);
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const onDrop = useCallback((acceptedFiles) => {
    const file = acceptedFiles[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target.result;
      if (typeof text !== 'string') {
        alert('Invalid file');
        return;
      }

      Papa.parse(text, {
        complete: (results) => {
          const { data } = results;
          if (data.length < 2) {
            alert('CSV must have headers + 1 row');
            return;
          }

          const headerRow = data[0];
          const rows = data.slice(1).filter(row => row.some(cell => cell?.trim() !== ''));

          setHeaders(headerRow);
          setCsvData(rows);
          setPreview(rows.slice(0, 5).map((row) => {
            const obj = {};
            headerRow.forEach((h, i) => {
              obj[h] = row[i] || '';
            });
            return obj;
          }));
        },
        header: false,
        skipEmptyLines: true,
      });
    };
    reader.onerror = () => alert('Failed to read file');
    reader.readAsText(file);
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: 'text/csv',
    multiple: false,
  });

  const importAll = async () => {
    setUploading(true);
    setSuccess(0);
    setErrors([]);

    const results = [];
    for (let i = 0; i < csvData.length; i++) {
      const row = csvData[i];
      const part = {};

      headers.forEach((header, idx) => {
        const key = columnMapping[header];
        if (key) {
          const raw = row[idx]?.trim() || '';
          if (key === 'price') {
            const n = parseFloat(raw);
            part[key] = raw === '' || Number.isNaN(n) ? null : n;
          } else {
            part[key] = raw;
          }
        }
      });

      part.dealership = dealership;

      if (!part.part_number) {
        results.push({ success: false, name: '(blank part number)', error: 'Missing Part Number' });
      } else {
        try {
          await dispatch(importCatalogPart(part));
          results.push({ success: true, name: part.part_number });
        } catch (err) {
          results.push({ success: false, name: part.part_number, error: err.message || 'Failed' });
        }
      }
    }

    await dispatch(getCatalogParts());

    const successes = results.filter(r => r.success).length;
    const fails = results.filter(r => !r.success);

    setSuccess(successes);
    setErrors(fails);
    setUploading(false);

    if (fails.length === 0 && successes > 0) {
      setTimeout(handleClose, 2000);
    }
  };

  if (!open) return null;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <strong>Mass Import Parts Catalog</strong>
        <Button onClick={handleClose} sx={{ position: 'absolute', right: 8, top: 8 }}>
          ×
        </Button>
      </DialogTitle>

      <DialogContent dividers>
        {csvData.length === 0 ? (
          <>
            <Alert severity="info" sx={{ mb: 2 }}>
              Your CSV needs a <strong>header row</strong>. Header names must match exactly (they are
              case-sensitive). Any column not listed below is ignored.
            </Alert>

            <Table size="small" sx={{ mb: 2 }}>
              <TableHead>
                <TableRow>
                  <TableCell><strong>Header</strong></TableCell>
                  <TableCell><strong>Required</strong></TableCell>
                  <TableCell><strong>What it does</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                <TableRow>
                  <TableCell><code>Part Number</code></TableCell>
                  <TableCell>Yes</TableCell>
                  <TableCell>The part number. Rows without one are skipped and reported.</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell><code>Part Description</code></TableCell>
                  <TableCell>Yes</TableCell>
                  <TableCell>The part description.</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell><code>Cost</code></TableCell>
                  <TableCell>No</TableCell>
                  <TableCell>
                    Becomes the part price. Leave blank for &quot;no price&quot; — that is different from 0.00.
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>

            <Alert severity="warning" sx={{ mb: 2 }}>
              Parts import into <strong>{dealership || 'the current store'}</strong>. Re-uploading is safe:
              a part number that already exists for this store is <strong>updated</strong> (description and
              price) rather than duplicated.
            </Alert>

            <Box sx={{ mb: 2 }}>
              <Typography variant="caption" color="textSecondary">Example</Typography>
              <Box
                component="pre"
                sx={{
                  m: 0, mt: 0.5, p: 1.5, bgcolor: '#f5f5f5', borderRadius: 1, fontSize: '0.8rem', overflowX: 'auto',
                }}
              >
{`Part Number,Part Description,Cost
19417577,ATF LV HP,10.84
PF64,ENGINE OIL FILTER,8.99
CV-2213,CV Axle Assembly,`}
              </Box>
            </Box>

            <Box
              {...getRootProps()}
              sx={{
                border: '2px dashed #ccc',
                borderRadius: 2,
                p: 4,
                textAlign: 'center',
                cursor: 'pointer',
                backgroundColor: isDragActive ? '#f0f8ff' : '#fafafa',
                '&:hover': { backgroundColor: '#f0f8ff' },
              }}
            >
              <input {...getInputProps()} />
              <p style={{ margin: 0, fontSize: '1.2rem' }}>
                {isDragActive ? 'Drop CSV here' : 'Drag & drop CSV or click to select'}
              </p>
              <p style={{ margin: '10px 0 0', fontSize: '0.9rem', color: '#666' }}>
                Columns: Part Number, Part Description, Cost
              </p>
            </Box>
          </>
        ) : (
          <>
            <Alert severity="info" sx={{ mb: 2 }}>
              Found {csvData.length} rows. Preview (first 5):
            </Alert>

            <Table size="small" sx={{ mb: 2, tableLayout: 'fixed', width: '100%' }}>
              <TableHead>
                <TableRow>
                  {headers.map((h, i) => (
                    <TableCell
                      key={i}
                      sx={
                        isPartNumberHeader(h)
                          ? { width: PART_NUMBER_MAX_WIDTH, maxWidth: PART_NUMBER_MAX_WIDTH }
                          : isDescriptionHeader(h)
                            ? { width: partDescriptionMaxWidthSx, maxWidth: partDescriptionMaxWidthSx }
                            : undefined
                      }
                    >
                      <strong>{h}</strong>
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {preview.map((row, i) => (
                  <TableRow key={i}>
                    {headers.map((h, j) => (
                      <TableCell
                        key={j}
                        sx={
                          isPartNumberHeader(h)
                            ? { maxWidth: PART_NUMBER_MAX_WIDTH, overflow: 'hidden' }
                            : isDescriptionHeader(h)
                              ? { maxWidth: partDescriptionMaxWidthSx, overflow: 'hidden' }
                              : undefined
                        }
                      >
                        {isPartNumberHeader(h)
                          ? <TruncatedPartNumber value={row[h]} />
                          : isDescriptionHeader(h)
                            ? <TruncatedPartDescription value={row[h]} />
                            : row[h]}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>

            {uploading && <LinearProgress sx={{ mb: 2 }} />}

            {success > 0 && (
              <Alert severity="success" sx={{ mb: 2 }}>
                Successfully imported {success} parts!
              </Alert>
            )}

            {errors.length > 0 && (
              <Alert severity="error">
                <strong>{errors.length} failed:</strong>
                <ul style={{ margin: '8px 0 0 0', paddingLeft: '20px' }}>
                  {errors.map((e, i) => (
                    <li key={i}>{e.name}: {e.error}</li>
                  ))}
                </ul>
              </Alert>
            )}
          </>
        )}
      </DialogContent>

      {csvData.length > 0 && (
        <DialogActions>
          <Button variant="outlined" onClick={() => setCsvData([])}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="success"
            onClick={importAll}
            disabled={uploading}
          >
            {uploading ? 'Importing...' : `Import ${csvData.length} Parts`}
          </Button>
        </DialogActions>
      )}
    </Dialog>
  );
};

export default CsvCatalogPartImporterModal;
